import type { ReactNode } from 'react'
import type { BrainMessage } from '@/types/brain'

interface MessageDetailProps {
  message: BrainMessage
  onClose: () => void
  onAck: (id: string) => void
  onReply?: (message: BrainMessage) => void
  onConvertToWorkOrder?: (message: BrainMessage) => void
  isSent?: boolean
}

function formatTimestamp(ts: number | string): string {
  return new Date(ts).toLocaleString()
}

function renderBody(body: unknown): ReactNode {
  if (body === null || body === undefined) return <span className="text-swiss-gray-500">—</span>

  if (typeof body === 'string') {
    // Try JSON pretty-print
    try {
      const parsed = JSON.parse(body)
      return (
        <pre className="text-[12px] font-swiss-mono whitespace-pre-wrap break-all">
          {JSON.stringify(parsed, null, 2)}
        </pre>
      )
    } catch {
      return <pre className="text-[12px] font-swiss-mono whitespace-pre-wrap break-all">{body}</pre>
    }
  }

  if (typeof body === 'object') {
    return (
      <dl className="flex flex-col gap-2">
        {Object.entries(body as Record<string, unknown>).map(([key, value]) => (
          <div key={key} className="flex flex-col gap-0.5">
            <dt className="text-[10px] font-medium uppercase tracking-widest text-swiss-gray-500">
              {key}
            </dt>
            <dd className="text-[12px] font-swiss-mono break-all">
              {typeof value === 'object'
                ? JSON.stringify(value, null, 2)
                : String(value ?? '—')}
            </dd>
          </div>
        ))}
      </dl>
    )
  }

  return <span className="text-[12px] font-swiss-mono">{String(body)}</span>
}

export function MessageDetail({ message, onClose, onAck, onReply, onConvertToWorkOrder, isSent }: MessageDetailProps) {
  return (
    <div className="border-b border-swiss-gray-200 bg-swiss-off-white px-6 py-5">
      {/* Header row */}
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-[15px] font-semibold pr-4">{message.subject}</h3>
        <button
          onClick={onClose}
          className="shrink-0 text-swiss-gray-500 hover:text-swiss-black transition-colors duration-100 cursor-pointer"
          aria-label="Close detail"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" />
          </svg>
        </button>
      </div>

      {/* Metadata */}
      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 mb-4">
        <div>
          <dt className="text-[10px] font-medium uppercase tracking-widest text-swiss-gray-500">From</dt>
          <dd className="text-[12px] font-swiss-mono">{message.from}</dd>
        </div>
        {message.to && (
          <div>
            <dt className="text-[10px] font-medium uppercase tracking-widest text-swiss-gray-500">To</dt>
            <dd className="text-[12px] font-swiss-mono">{message.to}</dd>
          </div>
        )}
        <div>
          <dt className="text-[10px] font-medium uppercase tracking-widest text-swiss-gray-500">Type</dt>
          <dd className="text-[12px] font-swiss-mono">{message.type}</dd>
        </div>
        <div>
          <dt className="text-[10px] font-medium uppercase tracking-widest text-swiss-gray-500">{isSent ? 'Sent' : 'Received'}</dt>
          <dd className="text-[12px] font-swiss-mono">{formatTimestamp(message.timestamp)}</dd>
        </div>
        <div>
          <dt className="text-[10px] font-medium uppercase tracking-widest text-swiss-gray-500">ID</dt>
          <dd className="text-[12px] font-swiss-mono truncate">{message.id}</dd>
        </div>
        {message.ttl_hours && (
          <div>
            <dt className="text-[10px] font-medium uppercase tracking-widest text-swiss-gray-500">TTL</dt>
            <dd className="text-[12px] font-swiss-mono">{message.ttl_hours}h</dd>
          </div>
        )}
      </dl>

      {/* Body */}
      <div className="pt-3 border-t border-swiss-gray-200">
        <p className="text-[10px] font-medium uppercase tracking-widest text-swiss-gray-500 mb-2">Body</p>
        <div className="bg-swiss-white border border-swiss-gray-200 p-4 max-h-96 overflow-y-auto">
          {renderBody(message.body)}
        </div>
      </div>

      {/* ACK button */}
      <div className="mt-4 flex flex-wrap gap-3 pt-4 border-t border-swiss-gray-200">
        {!isSent && (
          <button
            onClick={() => onAck(message.id)}
            className="px-4 py-2 text-[13px] font-medium bg-swiss-black text-swiss-white cursor-pointer hover:bg-swiss-gray-800 transition-colors duration-100"
            aria-label="Acknowledge this message"
          >
            Acknowledge
          </button>
        )}
        {onReply && (
          <button
            onClick={() => onReply(message)}
            className="px-4 py-2 text-[13px] font-medium border border-swiss-gray-200 text-swiss-black cursor-pointer hover:border-swiss-black transition-colors duration-100"
          >
            Reply
          </button>
        )}
        {onConvertToWorkOrder && message.type === 'request' && (
          <button
            onClick={() => onConvertToWorkOrder(message)}
            className="px-4 py-2 text-[13px] font-medium border border-swiss-gray-200 text-swiss-black cursor-pointer hover:border-swiss-black transition-colors duration-100"
          >
            Convert to Work Order
          </button>
        )}
      </div>
    </div>
  )
}
